/**
 * Impostazioni.
 *
 * Un menu a sinistra e una sezione per volta a destra, invece di una colonna
 * di undici schede in fila: chi apre le impostazioni cerca **una** cosa, e la
 * deve trovare nel titolo del menu, non scorrendo. Le sezioni sono in ordine
 * di quanto spesso servono, e ognuna si apre con due righe che dicono a cosa
 * serve.
 *
 * Lo stop generale resta sempre visibile in cima, qualunque sezione sia
 * aperta: è il bottone che un property manager deve trovare in tre secondi
 * quando qualcosa non gli torna.
 */

import { useState } from "react";
import {
  FileText,
  Landmark,
  PlugZap,
  ShieldCheck,
  Sparkles,
  User,
} from "lucide-react";
import { useStore } from "../lib/store.tsx";
import { cx } from "../ui/primitives.tsx";
import { StopGenerale } from "./impostazioni/stop-generale.tsx";
import { SezioneProfilo } from "./impostazioni/profilo.tsx";
import { SezioneGestionale } from "./impostazioni/gestionale.tsx";
import { SezioneModifiche } from "./impostazioni/modifiche.tsx";
import { SezioneTasse } from "./impostazioni/tasse.tsx";
import { SezioneDati } from "./impostazioni/dati.tsx";
import { Quaderno } from "./impostazioni/quaderno.tsx";
import { Memoria } from "./impostazioni/memoria.tsx";

type SezioneId =
  "profilo" | "gestionale" | "modifiche" | "tasse" | "otto" | "dati";

const SEZIONI: Array<{
  id: SezioneId;
  label: string;
  descrizione: string;
  Icona: typeof User;
}> = [
  {
    id: "profilo",
    label: "Profilo e accesso",
    descrizione: "Chi sei, la password, dove sei entrato.",
    Icona: User,
  },
  {
    id: "gestionale",
    label: "Gestionale",
    descrizione: "Il collegamento con Guesty e le sue chiavi.",
    Icona: PlugZap,
  },
  {
    id: "modifiche",
    label: "Limiti e sicurezza",
    descrizione: "Quante modifiche al giorno, e cosa decide Otto da solo.",
    Icona: ShieldCheck,
  },
  {
    id: "tasse",
    label: "Tasse",
    descrizione: "Il tuo regime fiscale: cambia il conto di ogni mese.",
    Icona: Landmark,
  },
  {
    id: "otto",
    label: "Come lavora Otto",
    descrizione: "Come rispondi agli ospiti e cosa ha imparato di te.",
    Icona: Sparkles,
  },
  {
    id: "dati",
    label: "I tuoi dati",
    descrizione: "Il fascicolo per il commercialista, e tutto in un file.",
    Icona: FileText,
  },
];

const CHIAVE_SEZIONE = "otto.impostazioni.sezione";

/**
 * Come si dispongono le schede di ogni sezione su uno schermo largo. In una
 * colonna sola da 560 px la pagina era metà vuota: le schede che si leggono
 * insieme stanno affiancate, quelle che hanno bisogno di spazio prendono la
 * riga intera (`xl:col-span-2` sulla scheda).
 */
const GRIGLIA: Record<SezioneId, string> = {
  profilo: "xl:grid-cols-2",
  gestionale: "xl:grid-cols-[1.5fr_1fr]",
  modifiche: "xl:grid-cols-[1fr_1.4fr] xl:items-start",
  tasse: "",
  otto: "xl:grid-cols-[1.5fr_1fr] xl:items-start",
  dati: "xl:grid-cols-2",
};

export function Settings() {
  const store = useStore();
  const [sezione, setSezione] = useState<SezioneId>(() => {
    try {
      const salvata = localStorage.getItem(CHIAVE_SEZIONE) as SezioneId | null;
      return SEZIONI.some((s) => s.id === salvata) ? salvata! : "profilo";
    } catch {
      return "profilo";
    }
  });

  const scegli = (id: SezioneId) => {
    setSezione(id);
    try {
      localStorage.setItem(CHIAVE_SEZIONE, id);
    } catch {
      /* senza storage si riparte dal profilo: niente di grave */
    }
  };

  const attiva = SEZIONI.find((s) => s.id === sezione)!;

  return (
    <div className="flex flex-col gap-6">
      <StopGenerale />

      <div className="grid gap-6 md:grid-cols-[240px_1fr] md:gap-10">
        {/* ── Il menu delle sezioni ──────────────────────────────────── */}
        <nav
          aria-label="Sezioni delle impostazioni"
          className="flex gap-1 overflow-x-auto md:sticky md:top-0 md:h-fit md:flex-col md:overflow-visible"
        >
          {SEZIONI.map(({ id, label, Icona }) => (
            <button
              key={id}
              type="button"
              onClick={() => scegli(id)}
              aria-current={id === sezione ? "page" : undefined}
              className={cx(
                "flex shrink-0 items-center gap-2.5 rounded-card px-3 py-2.5 text-left text-[13.5px] font-semibold",
                "transition-colors duration-quick",
                id === sezione
                  ? "bg-surface-muted text-ink"
                  : "text-ink-soft hover:bg-surface-muted/60 hover:text-ink",
              )}
            >
              <Icona
                size={16}
                className={id === sezione ? "text-ink" : "text-ink-faint"}
              />
              {label}
              {id === "gestionale" && !store.account?.pmsConnected ? (
                <span className="ml-auto size-2 rounded-full bg-warn" />
              ) : null}
            </button>
          ))}
        </nav>

        {/* ── La sezione aperta ──────────────────────────────────────── */}
        <div key={sezione} className="animate-rise min-w-0">
          <header className="mb-5">
            <h2 className="text-[20px] font-extrabold tracking-tight text-ink">
              {attiva.label}
            </h2>
            <p className="mt-0.5 text-[13.5px] text-ink-soft">
              {attiva.descrizione}
            </p>
          </header>
          <div className={cx("grid gap-4", GRIGLIA[sezione])}>
            {sezione === "profilo" ? <SezioneProfilo /> : null}
            {sezione === "gestionale" ? <SezioneGestionale /> : null}
            {sezione === "modifiche" ? <SezioneModifiche /> : null}
            {sezione === "tasse" ? <SezioneTasse /> : null}
            {sezione === "otto" ? (
              <>
                <Quaderno />
                <Memoria />
              </>
            ) : null}
            {sezione === "dati" ? <SezioneDati /> : null}
          </div>
        </div>
      </div>
    </div>
  );
}
